/**
 * Video Call Controls Component
 * UI for starting instant or custom named meetings
 */

import React, { useState } from 'react';
import { Button, Input, Space, Card, Typography, Divider, message } from 'antd';
import { VideoCameraOutlined, LinkOutlined } from '@ant-design/icons';
import { useStartVideoCall } from '@/hooks/useVideoCall';
import { useAuth } from '@/context/AuthContext';
import './VideoCallControls.css';

const { Title, Text, Paragraph } = Typography;

interface VideoCallControlsProps {
  onMeetingStarted?: (roomName: string, meetingId: string) => void;
}

export const VideoCallControls: React.FC<VideoCallControlsProps> = ({
  onMeetingStarted,
}) => {
  const [customRoomName, setCustomRoomName] = useState('');
  const { mutate: startMeeting, isPending } = useStartVideoCall();
  const { user } = useAuth();

  const generateRandomRoom = () => {
    const adjectives = ['quick', 'happy', 'bright', 'calm', 'bold'];
    const nouns = ['meeting', 'sync', 'chat', 'call', 'huddle'];
    const random = Math.floor(Math.random() * 1000);
    return `${adjectives[Math.floor(Math.random() * adjectives.length)]}-${
      nouns[Math.floor(Math.random() * nouns.length)]
    }-${random}`;
  };

  const handleStartMeeting = (roomName?: string) => {
    const finalRoomName = roomName || generateRandomRoom();
    const displayName = user?.full_name || user?.email || 'Anonymous';

    startMeeting(
      {
        room_name: finalRoomName,
        display_name: displayName,
        notify_team: true,
      },
      {
        onSuccess: (response) => {
          onMeetingStarted?.(response.room_name, response.meeting_id);
        },
      }
    );
  };

  const handleQuickMeeting = () => {
    handleStartMeeting();
  };

  const handleCustomMeeting = () => {
    if (!customRoomName.trim()) {
      message.warning('Please enter a room name');
      return;
    }
    handleStartMeeting(customRoomName.trim());
  };

  return (
    <div className="video-call-controls">
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Quick Start */}
          <div>
            <Title level={4}>
              <VideoCameraOutlined /> Start a Video Call
            </Title>
            <Paragraph type="secondary">
              Instantly create a video meeting room and invite your team
            </Paragraph>
            <Button
              type="primary"
              size="large"
              icon={<VideoCameraOutlined />}
              loading={isPending}
              onClick={handleQuickMeeting}
              block
            >
              Start Instant Meeting
            </Button>
          </div>

          <Divider>OR</Divider>

          {/* Custom Room Name */}
          <div>
            <Title level={5}>Create Named Meeting</Title>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Choose a custom name for your meeting room
            </Paragraph>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="my-meeting-room"
                value={customRoomName}
                onChange={(e) => setCustomRoomName(e.target.value)}
                onPressEnter={handleCustomMeeting}
                size="large"
                prefix={<LinkOutlined />}
              />
              <Button
                type="primary"
                size="large"
                onClick={handleCustomMeeting}
                loading={isPending}
              >
                Start
              </Button>
            </Space.Compact>
            <Text
              type="secondary"
              style={{ fontSize: 12, marginTop: 8, display: 'block' }}
            >
              Room name will be: thilo-{customRoomName || 'room-name'}
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};
